import { AsyncButton } from '@framework/ui/components/button/AsyncButton.tsx';
import {
  type ButtonResponsiveProps,
  getResponsiveButtonProps,
} from '@framework/ui/components/button/getResponsiveButtonProps.tsx';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { useFormAugmentedContext } from '../augmented/FormAugmentedContext.tsx';
import { type IFormTransformOnSubmitFn } from '../IFormTransformOnSubmitFn.ts';

export interface IManagedSubmitButtonProps
  extends Omit<ButtonResponsiveProps, 'onClick'> {
  submitArgs?: any;
  transformOnSubmit?: IFormTransformOnSubmitFn;
  onValid?: (
    /**
     * Valori ritornati dalla funzione di submit passata al form
     */
    submitResult?: any,
    /**
     * valori originali passati alla onSubmit del form
     */
    originalValue?: any,
  ) => void;
  onInvalid?: () => void;

  text?: React.ReactNode;
  // iconRight?: React.ReactNode;
  iconRightComponent?: React.ComponentType;
  iconRightAriaLabel?: string;
}

/**
 * Bottone che gestisce programmaticamente il submit del form
 * permettendo di chiamare una funzione specifica in caso di validità
 * o invalidità del form.
 */
export function ManagedSubmitButton(props: IManagedSubmitButtonProps) {
  const { onInvalid, onValid, transformOnSubmit, ...otherProps } = props;

  const isMobile = useBreakpoint(down('md'));
  const buttonResponsiveProps = useMemo(() => {
    return getResponsiveButtonProps(
      otherProps satisfies ButtonResponsiveProps,
      isMobile,
    );
  }, [otherProps, isMobile]);

  const augmented = useFormAugmentedContext();

  const handleSubmit = useCallback(
    async (e: React.MouseEvent<HTMLElement>) => {
      try {
        const submitResult = await augmented.submitForm.current?.(
          e,
          transformOnSubmit,
        );
        onValid?.(submitResult?.result, submitResult?.originalValue);
      } catch (error) {
        onInvalid?.();
      }
    },
    [augmented.submitForm, onInvalid, onValid, transformOnSubmit],
  );

  return <AsyncButton {...buttonResponsiveProps} onClick={handleSubmit} />;
}
